const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('ponzinomics_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...authHeaders(),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ message: 'Request failed' }))) as {
      message: string;
    };
    throw new Error(err.message ?? `Request failed with status ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export interface CreditBalance {
  paidCredits: number;
  freeCreditsUsed: number;
  freeCreditsLimit: number;
  totalCallsThisPeriod: number;
}

export interface UsageData {
  callsThisMonth: number;
  freeCreditsRemaining: number;
  paidCreditsRemaining: number;
}

export interface CreditPackage {
  id: string;
  credits: number;
  priceUsd: number;
  originalPriceUsd: number;
  discountPercent: number;
  label: string;
}

export interface PaymentRecord {
  id: string;
  date: string;
  amountUsd: number;
  credits: number;
  provider: 'stripe' | 'solana';
  status: 'completed' | 'pending' | 'failed';
}

export interface StripeCheckoutResult {
  url: string;
}

export interface SolanaPaymentResult {
  reference: string;
  recipient: string;
  amount: number;
  label: string;
  solanaPay: string;
}

export interface SolanaVerifyResult {
  verified: boolean;
  credits?: number;
}

export const billingApi = {
  getBalance: (projectId: string): Promise<CreditBalance> =>
    request(`/billing/balance/${projectId}`),

  getUsage: (projectId: string): Promise<UsageData> => request(`/billing/usage/${projectId}`),

  getPackages: (): Promise<CreditPackage[]> => request('/billing/packages'),

  getPaymentHistory: (projectId: string): Promise<PaymentRecord[]> =>
    request(`/billing/history/${projectId}`),

  createStripeCheckout: (projectId: string, packageId: string): Promise<StripeCheckoutResult> =>
    request(`/billing/checkout/stripe/${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ packageId }),
    }),

  createSolanaPayment: (projectId: string, packageId: string): Promise<SolanaPaymentResult> =>
    request(`/billing/checkout/solana/${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ packageId }),
    }),

  verifySolanaPayment: (reference: string): Promise<SolanaVerifyResult> =>
    request(`/billing/verify/solana/${reference}`),
};
